<template>
  <v-row>
    <loading-spinner v-if="loading"></loading-spinner>
    <template v-else>
      <v-col cols="12">
        <v-card>
          <v-row dense>
            <v-col cols="auto" class="my-auto">
              <v-card-title
                >{{ profile.companyName }}'s Annual Review</v-card-title
              >
            </v-col>
            <v-spacer> </v-spacer>
            <v-col cols="auto">
              <v-card-text>
                <v-text-field
                  v-model="search"
                  placeholder="Search for an employee"
                  outlined
                  prepend-inner-icon="search"
                  hide-details
                  clearable
                  dense
                >
                </v-text-field>
              </v-card-text>
            </v-col>
            <v-col class="pr-4 my-auto">
              <v-select
                v-model="filterId"
                dense
                outlined
                hide-details
                :items="items"
              >
              </v-select>
            </v-col>
          </v-row>
          <v-card-text>
            <v-data-table
              :loading="loading"
              :headers="headers"
              :items="list"
              :footer-props="{
                'items-per-page-options': [10, 25, 50, 100]
              }"
            >
              <template v-slot:item.employee="{ item }">
                <v-row>
                  <v-col cols="auto" class="px-1">
                    <user-avatar :size="32" :user="item.user"></user-avatar>
                  </v-col>
                  <v-col class="px-0">
                    <div class="font-weight-medium">
                      {{ item.user.fullName }}
                    </div>
                    <div class="caption">{{ item.user.jobTitle }}</div>
                  </v-col>
                </v-row>
              </template>
              <template v-slot:item.manager="{ item }">
                <v-row>
                  <v-col cols="auto" class="px-1">
                    <user-avatar :size="32" :user="item.manager"></user-avatar>
                  </v-col>
                  <v-col class="px-0">
                    <div class="font-weight-medium">
                      {{ item.manager.fullName }}
                    </div>
                    <div class="caption">{{ item.manager.jobTitle }}</div>
                  </v-col>
                </v-row>
              </template>
              <template v-slot:item.managerQuestionsComplete="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      class="mr-2"
                      :color="
                        item.userQuestionsComplete ? 'success' : 'lighttext'
                      "
                    >
                      {{
                        item.userQuestionsComplete ? "check_circle" : "cancel"
                      }}
                    </v-icon>
                  </template>
                  <div class="tooltip-limit-width">
                    {{ item.user.fullName }}
                    {{
                      item.userQuestionsComplete
                        ? " has completed"
                        : " hasn't completed"
                    }}
                  </div>
                </v-tooltip>
                <br />
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      :color="
                        item.managerQuestionsComplete ? 'success' : 'lighttext'
                      "
                    >
                      {{
                        item.managerQuestionsComplete
                          ? "check_circle"
                          : "cancel"
                      }}
                    </v-icon>
                  </template>
                  <div class="tooltip-limit-width">
                    {{ item.manager.fullName }}
                    {{
                      item.managerQuestionsComplete
                        ? " has completed"
                        : " hasn't completed"
                    }}
                  </div>
                </v-tooltip>
              </template>
              <template v-slot:item.managerSignoffComplete="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      class="mr-2"
                      :color="
                        item.userSignoffComplete ? 'success' : 'lighttext'
                      "
                    >
                      {{ item.userSignoffComplete ? "check_circle" : "cancel" }}
                    </v-icon>
                  </template>
                  <div class="tooltip-limit-width">
                    {{ item.user.fullName }}
                    {{
                      item.userQuestionsComplete
                        ? " has signed off"
                        : " hasn't signed off"
                    }}
                  </div>
                </v-tooltip>
                <br />
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      :color="
                        item.managerSignoffComplete ? 'success' : 'lighttext'
                      "
                    >
                      {{
                        item.managerSignoffComplete ? "check_circle" : "cancel"
                      }}
                    </v-icon>
                  </template>
                  <div class="tooltip-limit-width">
                    {{ item.manager.fullName }}
                    {{
                      item.managerSignoffComplete
                        ? " has signed off"
                        : " hasn't signed off"
                    }}
                  </div>
                </v-tooltip>
              </template>
              <template v-slot:item.payBonus="{ item }">
                <span
                  :class="(item.payBonus ? 'success' : 'lighttext') + '--text'"
                >
                  {{ item.payBonus ? "Yes" : "No" }}
                </span>
              </template>
              <template v-slot:item.userRatingSort="{ item }">
                {{ item.userRatingName }}
              </template>
              <template v-slot:item.complete="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      class="mr-2"
                      :color="item.complete ? 'success' : 'lighttext'"
                    >
                      {{ item.complete ? "check_circle" : "cancel" }}
                    </v-icon>
                  </template>
                  <div class="tooltip-limit-width">
                    {{ item.user.fullName }}'s Review is
                    {{ item.complete ? " Complete" : " Incomplete" }}
                  </div>
                </v-tooltip>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-menu absolute v-if="canComplete(item)">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon>
                      <v-icon>more_vert</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <!-- <v-list-item
                      :to="
                        `/features/annual-reviews/${item.user.id}/${item.id}`
                      "
                    >
                      <v-list-item-title>View</v-list-item-title>
                    </v-list-item> -->
                    <v-list-item @click="complete(item)">
                      <v-list-item-title>Complete</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </template>
  </v-row>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "AnnualReviewOverviewTable",
  components: {},
  data: () => ({
    loading: true,
    saving: false,
    search: "",
    headers: [
      { text: "Employee", value: "employee" },
      { text: "Manager", value: "manager", class: "text-no-wrap" },
      { text: "QA", value: "managerQuestionsComplete", class: "text-no-wrap" },
      {
        text: "Signoff",
        value: "managerSignoffComplete",
        class: "text-no-wrap"
      },
      { text: "Bonus/Pay", value: "payBonus", class: "text-no-wrap" },
      { text: "Rating", value: "userRatingSort" },
      { text: "Done", value: "complete", class: "text-no-wrap" },
      { text: "Action", value: "actions", sortable: false }
    ],
    filterId: null
  }),
  watch: {
    items(val, oldVal) {
      if (val != oldVal && this.filterId == null && (val || []).length > 0) {
        this.filterId = val[0].value;
      }
    }
  },
  computed: {
    items() {
      return this._.uniqBy(this.annual_reviews, "annualReviewStartDate").map(
        x => ({
          value: x.annualReviewStartDate,
          text: `${this.$options.filters.year(
            x.annualReviewStartDate
          )} Annual Reviews`
        })
      );
    },
    canComplete() {
      return item => {
        return (
          item.managerSignoffComplete &&
          item.userSignoffComplete &&
          !item.complete
        );
      };
    },
    list() {
      let reviews = this.annual_reviews.filter(
        x => x.annualReviewStartDate == this.filterId
      );
      if (this.search) {
        let search = this.search.toLowerCase();
        reviews = reviews.filter(
          x =>
            x.user.fullName.toLowerCase().indexOf(search) >= 0 ||
            x.manager.fullName.toLowerCase().indexOf(search) >= 0
        );
      }
      return reviews;
    },
    ...mapGetters({
      annual_reviews: "annualreview/list",
      profile: "profile/profile"
    })
  },
  created() {
    this.getData();
  },
  methods: {
    getData() {
      this.loading = true;
      this.$store
        .dispatch("annualreview/getAllAnnualReviews")
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading your annual reviews."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    complete(item) {
      this.saving = true;
      this.$store
        .dispatch("annualreview/complete", item)
        .then(() => {
          this.$root.$snackbarSuccess("Annual review completed");
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while complete the annual review."
          );
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>
